/**
 * Format cents to a USD display string.
 *
 * The store is USD-based: stored cents are USD cents and rendered directly as
 * USD. (Any legacy `opts` second argument is accepted and ignored.)
 */
export const formatPrice = (cents) => {
  const amount = (cents || 0) / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

/** Format ISO date string */
export const formatDate = (iso) =>
  new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(iso));

/**
 * VIP membership pricing for a variant.
 *
 * The model: `priceInCents` is the VIP (member) price; `comparePriceInCents`
 * (when higher) is the retail price non-members pay. Members get the VIP price;
 * everyone else pays retail and sees the VIP price as a locked upsell.
 *
 * Returns the price actually charged plus both reference prices and savings so
 * UI can render "VIP $X / retail $Y — save $Z".
 */
export const memberPrice = (variant, isMember) => {
  const vip = variant?.priceInCents || 0;
  const retail = (variant?.comparePriceInCents && variant.comparePriceInCents > vip)
    ? variant.comparePriceInCents
    : vip;
  const hasVipDeal = retail > vip;
  return {
    vip,
    retail,
    pay: isMember ? vip : retail,        // what THIS shopper is charged
    savings: retail - vip,               // member savings vs retail
    hasVipDeal,                          // is there a member discount at all?
    showUpsell: hasVipDeal && !isMember, // non-member who could save by joining
  };
};

/** True when a user object carries an active VIP membership. */
export const isVipMember = (user) => !!(user && user.membership && user.membership.active);

/** Generic vial photo, used as the fallback for any missing product image. */
export const VIAL_FALLBACK = process.env.PUBLIC_URL + '/images/vial-hero.jpg';

/**
 * Per-product vial photo (label printed with the real product name).
 * Falls back to the generic vial when no slug is available.
 */
export const productImage = (slug) =>
  slug ? `${process.env.PUBLIC_URL}/images/products/${slug}.png` : VIAL_FALLBACK;

/** onError handler: swap a broken product image for the generic vial once. */
export const onImageError = (e) => {
  if (e.target.src !== VIAL_FALLBACK) e.target.src = VIAL_FALLBACK;
};

/** Capitalize first letter */
export const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

/** Map order status to badge class */
export const statusBadge = (status) => {
  const map = {
    paid: 'badge-green', delivered: 'badge-green',
    pending: 'badge-yellow', processing: 'badge-yellow', shipped: 'badge-yellow',
    cancelled: 'badge-red', refunded: 'badge-red',
  };
  return map[status] || 'badge-gray';
};
